import PropTypes from "prop-types"
import { useState } from "react"

import { Bound } from "lib/@components/binding/Bound"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { TermsAndConditions } from "minimals-template/components/terms-and-conditions"
import { ClientSelect } from "minimals-template/pages/auth/ClientSelect"
import { NoLicense } from "minimals-template/pages/auth/NoLicense"
import { useSelectedClient } from "minimals-template/pages/auth/use-selected-client"
import { AuthGuardFailure } from "minimals-template/components/guards/auth-guard-failure"
import { useLoadingTimeout } from "minimals-template/components/guards/use-loading-timeout"

// FIQ-578: if the boot queries hang this long we stop spinning and offer recovery
const AUTH_GUARD_STUCK_MS = 30000

export function AuthenticatedAuthGuard({ children }) {
    const {
        clients,
        setSelectedClient,
        selectedClient,
        resetSelectedClient,
        clientsLoading,
        loading,
        selectedClientInitialised,
        loadFailed,
        retry,
        switching,
    } = useSelectedClient()
    const [retryCount, setRetryCount] = useState(0)
    const stuck = useLoadingTimeout(clientsLoading || loading, AUTH_GUARD_STUCK_MS, retryCount)

    if (switching) {
        return <LoadingScreen description="Switching Client" />
    }

    // FIQ-578: failed or hung boot queries previously left the user on the
    // "Auth Guard" spinner forever, recoverable only by clearing site data.
    // Surface an explicit error with retry and logout paths instead.
    if (loadFailed || stuck) {
        return <AuthGuardFailure onRetry={retryLoad} />
    }

    if (clientsLoading || loading) {
        return <LoadingScreen description="Auth Guard" />
    }

    return (
        <Bound
            clients={clients}
            selectedClient={selectedClient}
            setSelectedClient={setSelectedClient}
            resetSelectedClient={resetSelectedClient}
            selectedClientInitialised={selectedClientInitialised}
        >
            <ClientSelect>
                <TermsAndConditions>
                    <NoLicense>{children}</NoLicense>
                </TermsAndConditions>
            </ClientSelect>
        </Bound>
    )

    function retryLoad() {
        // Restart the stuck-timer window so the spinner returns while the retry runs
        setRetryCount((count) => count + 1)
        retry()
    }
}

AuthenticatedAuthGuard.propTypes = {
    children: PropTypes.node,
}
